import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { BrowserStorage } from 'UTILS/browserStorage'

import { useHasSocialAccounts } from '../useHasSocialAccount'

// undefined - social accounts have not finished yet
type ShouldShow = boolean | undefined

const ShouldShowWelcomePageValueContext = createContext<ShouldShow>(undefined)
const ShouldShowWelcomePageDispatchContext = createContext<(shouldShow: ShouldShow) => void>(
  () => false,
)

const WELCOME_PAGE_VISITED = 'v2/welcome-page-visited'
const sessionStorage = new BrowserStorage(window.sessionStorage)
const isWelcomePageVisited = (): boolean => sessionStorage.getItem(WELCOME_PAGE_VISITED) === 'true'

export const useMarkWelcomePageVisited = (): Dispatch<boolean> => {
  return useCallback((visited: boolean) => {
    sessionStorage.setItem(WELCOME_PAGE_VISITED, visited.toString())
  }, [])
}

export const useSetShouldShowWelcomePage = (): Dispatch<boolean> => {
  return useContext(ShouldShowWelcomePageDispatchContext)
}

export const ShouldShowWelcomePageProvider: FC<PropsWithChildren> = ({ children }) => {
  const [shouldShow, setShouldShow] = useState<ShouldShow>(() => {
    return isWelcomePageVisited() ? false : undefined
  })

  const { hasSocialAccounts, isLoading } = useHasSocialAccounts()

  useEffect(() => {
    if (hasSocialAccounts === undefined || isLoading) return

    setShouldShow((current) => (current === undefined ? !hasSocialAccounts : current))
  }, [isLoading, hasSocialAccounts])

  return (
    <ShouldShowWelcomePageValueContext.Provider value={shouldShow}>
      <ShouldShowWelcomePageDispatchContext.Provider value={setShouldShow}>
        {children}
      </ShouldShowWelcomePageDispatchContext.Provider>
    </ShouldShowWelcomePageValueContext.Provider>
  )
}
